import { useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import exportToCSV from "../components/exportToCSV";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

export async function loader() {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Response(
      JSON.stringify({
        message: "You must be logged in to access this resource.",
      }),
      { status: 401 }
    );
  }

  const response = await fetch(`${apiUrl}/ct/stock`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-App-Secret": process.env.REACT_APP_APP_SECRET,
    },
  });

  if (!response.ok) {
    throw new Response(
      JSON.stringify({ message: "Could not fetch stock data." }),
      { status: response.status }
    );
  }

  const stockData = await response.json();
  return stockData;
}

export default function StocksManagement() {
  const stockData = useLoaderData();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    product_id: "",
    product_name: "",
    size: "",
    in_stock: "",
  });
  const [editRowId, setEditRowId] = useState(null);
  const [editableData, setEditableData] = useState({});

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Filter stock items based on the filter inputs.
  const filteredData = stockData.filter((item) =>
    Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      return String(item[key]).toLowerCase().includes(value.toLowerCase());
    })
  );

  const handleEditClick = (id, rowData) => {
    setEditRowId(id);
    // Allow editing the in_stock and stock fields.
    setEditableData({
      in_stock: rowData.in_stock ? "true" : "false",
      stock: rowData.stock,
    });
  };

  const handleSaveClick = async (id) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("You must be logged in to save changes.");
        navigate("/login");
        return;
      }

      const response = await fetch(`${apiUrl}/ct/stock/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-App-Secret": process.env.REACT_APP_APP_SECRET,
        },
        body: JSON.stringify({
          in_stock: editableData.in_stock === "true",
          stock: Number(editableData.stock),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update the stock data");
      }

      const updatedItem = await response.json();

      alert(updatedItem.message);
      setEditRowId(null);
      navigate(".", { replace: true });
    } catch (err) {
      console.error("Error updating stock data:", err);
      alert("An error occurred while saving the stock data.");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="stocks-container">
      <h1>Stock Management</h1>

      <div className="filters">
        <label>
          Product ID:
          <input
            type="text"
            name="product_id"
            value={filters.product_id}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Product Name:
          <input
            type="text"
            name="product_name"
            value={filters.product_name}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Size:
          <input
            type="text"
            name="size"
            value={filters.size}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          In Stock:
          <select
            name="in_stock"
            value={filters.in_stock}
            onChange={handleFilterChange}
          >
            <option value="">All</option>
            <option value="true">In Stock</option>
            <option value="false">Out of Stock</option>
          </select>
        </label>
      </div>

      <div className="export-button">
        <button
          onClick={() => exportToCSV(filteredData, "stock.csv")}
          className="export-btn"
        >
          Export CSV
        </button>
      </div>

      <table className="management-table">
        <thead>
          <tr>
            <th>Product ID</th>
            <th>Product Name</th>
            <th>Size</th>
            <th>In Stock</th>
            <th>Stock</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item) => {
            // Create a unique identifier for each row using product_id and size.
            const rowId = `${item.product_id}-${item.size}`;
            return (
              <tr key={rowId}>
                {editRowId === rowId ? (
                  <>
                    <td>{item.product_id}</td>
                    <td>{item.product_name}</td>
                    <td>{item.size}</td>
                    <td>
                      <select
                        name="in_stock"
                        value={editableData.in_stock}
                        onChange={handleEditChange}
                      >
                        <option value="true">In Stock</option>
                        <option value="false">Out of Stock</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        name="stock"
                        value={editableData.stock}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <button
                        className="save-btn"
                        onClick={() => handleSaveClick(rowId)}
                      >
                        Save
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{item.product_id}</td>
                    <td>{item.product_name}</td>
                    <td>{item.size}</td>
                    <td>{item.in_stock ? "Yes" : "No"}</td>
                    <td>{item.stock}</td>
                    <td>
                      <button
                        className="edit-btn"
                        onClick={() => handleEditClick(rowId, item)}
                      >
                        Edit
                      </button>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
